/**
 * AG-UI message conversion + history fetch.
 *
 * Converts the backend's flat AG-UI message stream (alternating
 * user/assistant/tool entries) into the parts-based ThreadMessageLike
 * shape assistant-ui expects.
 */
import type { ThreadMessageLike } from "@assistant-ui/react";
import type { AssistantMessageCustomMetadata, TurnFeedback } from "~/components/assistant/types";
import { apiClient, isApiError } from "~/utils/apiClient";

interface AguiToolCall {
  id: string;
  type: string;
  function: { name: string; arguments: string };
}

export interface AguiMessage {
  role: "user" | "assistant" | "tool";
  content: string | null;
  id?: string;
  toolCalls?: AguiToolCall[];
  toolCallId?: string;
  error?: string | null;
  cancelled?: boolean;
  turnId?: string;
  turnFeedback?: TurnFeedback | null;
}

type ContentPart =
  | { readonly type: "text"; readonly text: string }
  | {
      readonly type: "tool-call";
      readonly toolCallId: string;
      readonly toolName: string;
      readonly args: Record<string, unknown>;
      readonly argsText: string;
      result?: string | null;
      isError?: boolean;
    };

/**
 * Merges consecutive assistant + tool messages into a single assistant
 * message per user turn, matching the structure assistant-ui expects.
 */
export function convertAguiToThreadMessages(aguiMessages: AguiMessage[]): ThreadMessageLike[] {
  const result: ThreadMessageLike[] = [];
  let currentParts: ContentPart[] = [];
  let lastTurnId: string | undefined;
  let lastTurnFeedback: TurnFeedback | null | undefined;
  let pendingCancelled = false;

  const flushAssistant = () => {
    if (currentParts.length > 0 || pendingCancelled) {
      const custom: AssistantMessageCustomMetadata = {};
      if (lastTurnId) custom.turnId = lastTurnId;
      if (lastTurnFeedback) custom.feedback = lastTurnFeedback;
      if (pendingCancelled) custom.cancelled = true;
      result.push({
        role: "assistant",
        content: currentParts as ThreadMessageLike["content"],
        metadata: Object.keys(custom).length > 0 ? { custom } : undefined,
      });
      currentParts = [];
      pendingCancelled = false;
    }
  };

  for (const msg of aguiMessages) {
    if (msg.role === "user") {
      flushAssistant();
      lastTurnId = msg.turnId;
      lastTurnFeedback = msg.turnFeedback;
      result.push({
        role: "user",
        content: msg.content ?? "",
        metadata: msg.turnId ? { custom: { turnId: msg.turnId } } : undefined,
      });
    } else if (msg.role === "assistant") {
      if (msg.cancelled) {
        pendingCancelled = true;
        continue;
      }
      if (msg.toolCalls) {
        for (const tc of msg.toolCalls) {
          let args: Record<string, unknown> = {};
          try {
            args = JSON.parse(tc.function.arguments);
          } catch {
            // keep empty args if parse fails
          }
          currentParts.push({
            type: "tool-call",
            toolCallId: tc.id,
            toolName: tc.function.name,
            args,
            argsText: tc.function.arguments,
          });
        }
      }
      if (msg.content && !msg.toolCalls) {
        currentParts.push({ type: "text", text: msg.content });
      }
    } else if (msg.role === "tool") {
      for (let i = currentParts.length - 1; i >= 0; i--) {
        const part = currentParts[i];
        if (part.type === "tool-call" && part.toolCallId === msg.toolCallId) {
          part.result = msg.content;
          if (msg.error) part.isError = true;
          break;
        }
      }
    }
  }

  flushAssistant();
  return result;
}

/**
 * Fetches conversation history from `/conversations/{id}/connect` and
 * converts the AG-UI message stream into ThreadMessageLike[].
 *
 * Returns an empty array if the conversation has no messages or the
 * backend returns 400 (e.g. the conversation was just created and has
 * no committed history yet).
 */
export async function fetchConversationHistory(
  baseUrl: string,
  conversationId: string
): Promise<ThreadMessageLike[]> {
  try {
    const resp = await apiClient<{ state: unknown; messages: AguiMessage[] }>(
      `${baseUrl}/conversations/${conversationId}/connect`
    );
    return resp.messages.length ? convertAguiToThreadMessages(resp.messages) : [];
  } catch (error) {
    if (isApiError(error) && error.status === 400) {
      return [];
    }
    throw error;
  }
}
